import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n/config";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Mic,
  Sparkles,
  Play,
  Pause,
  Square,
  Loader2,
  Crown,
  Plus,
  Trash2,
  BookOpen,
  Network,
  Layers,
  Lightbulb,
  FileText,
  Upload,
  Image as ImageIcon,
  Type,
  Bookmark,
  Accessibility,
  Wand2,
} from "lucide-react";
import { Clapperboard } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { StudyVideoStudio } from "@/components/StudyVideoStudio";
import { supabase } from "@/integrations/supabase/client";
import {
  generatePodcast,
  generateMindMap,
  transcribeAudio,
  extractTextFromImage,
  synthesizeSpeech,
} from "@/lib/content.functions";
import { MindMapView, type MindNode } from "@/components/mind-map-view";
import { MindMapDownloadButton } from "@/components/MindMapDownloadButton";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MediaAttachButton } from "@/components/MediaAttachButton";
import { FolderedLibrary } from "@/components/FolderedLibrary";
import { Library } from "lucide-react";
import { IllustratedMapStudio } from "@/components/IllustratedMapStudio";
import { PresentationStudio } from "@/components/PresentationStudio";
import { Presentation } from "lucide-react";

export const Route = createFileRoute("/_app/tools")({
  head: () => ({
    meta: [
      { title: i18n.t("tools.meta.title") },
      {
        name: "description",
        content: i18n.t("tools.meta.description"),
      },
    ],
  }),
  component: ToolsPage,
});

function ToolsPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 px-5 pt-8">
      <PageHeader
        tone="lilac"
        icon={<Wand2 className="h-5 w-5" />}
        kicker={t("tools.page.kicker")}
        title={t("tools.page.title")}
        subtitle={t("tools.page.subtitle")}
      />

      <Tabs defaultValue="podcast" className="w-full">
        <TabsList className="flex w-full justify-start gap-1 overflow-x-auto sm:grid sm:grid-cols-5">
          <TabsTrigger value="podcast" className="gap-1.5"><Mic className="h-3.5 w-3.5" /> {t("tools.tabs.podcast")}</TabsTrigger>
          <TabsTrigger value="video" className="gap-1.5"><Clapperboard className="h-3.5 w-3.5" /> {t("video.tab")}</TabsTrigger>
          <TabsTrigger value="maps" className="gap-1.5"><Network className="h-3.5 w-3.5" /> {t("tools.tabs.maps")}</TabsTrigger>
          <TabsTrigger value="slides" className="gap-1.5"><Presentation className="h-3.5 w-3.5" /> {t("video.slidesTab")}</TabsTrigger>
          <TabsTrigger value="guide" className="gap-1.5"><BookOpen className="h-3.5 w-3.5" /> {t("tools.tabs.guide")}</TabsTrigger>
        </TabsList>

        <TabsContent value="podcast" className="mt-4 space-y-4">
          <PodcastPanel />
        </TabsContent>
        <TabsContent value="video" className="mt-4 space-y-4">
          <StudyVideoStudio />
        </TabsContent>
        <TabsContent value="maps" className="mt-4 space-y-4">
          <MapsPanel />
        </TabsContent>
        <TabsContent value="slides" className="mt-4 space-y-4">
          <PresentationStudio />
        </TabsContent>
        <TabsContent value="guide" className="mt-4 space-y-4">
          <MapsGuide />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ============================================================ */
/*  PODCAST                                                     */
/* ============================================================ */

type PodcastScript = {
  title: string;
  intro: string;
  segments: { speaker: "A" | "B"; text: string }[];
  outro: string;
  chapters?: { title: string; startSegment: number }[];
};

function PodcastPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [topic, setTopic] = useState("");
  const [level, setLevel] = useState<"base" | "medio" | "avanzato">("medio");
  const [minutes, setMinutes] = useState(6);
  const [current, setCurrent] = useState<PodcastScript | null>(null);
  const [sourceText, setSourceText] = useState("");
  const [attachedName, setAttachedName] = useState<string | null>(null);
  const run = useServerFn(generatePodcast);

  const { data: history = [] } = useQuery({
    queryKey: ["podcasts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("podcasts")
        .select("id,title,topic,level,duration_estimate_min,script,created_at")
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
  });

  const mutation = useMutation({
    mutationFn: async () =>
      run({
        data: {
          topic,
          level,
          minutes,
          sourceText: sourceText.trim() ? sourceText.trim() : undefined,
        },
      }),
    onSuccess: (r) => {
      setCurrent(r.script);
      qc.invalidateQueries({ queryKey: ["podcasts"] });
      toast.success(t("tools.podcast.toastReady"));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-4">
      <Card className="card-soft border-0 p-5">
        <div className="mb-4 flex items-start gap-3">
          <div className="rounded-2xl bg-primary/10 p-2 text-primary"><Mic className="h-5 w-5" /></div>
          <div>
            <p className="font-semibold">{t("tools.podcast.card.title")}</p>
            <p className="text-sm text-muted-foreground">{t("tools.podcast.card.desc")}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pod-topic">{t("tools.podcast.topicLabel")}</Label>
            <Input
              id="pod-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t("tools.podcast.topicPlaceholder")}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("tools.podcast.levelLabel")}</Label>
              <Select value={level} onValueChange={(v) => setLevel(v as typeof level)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="base">{t("tools.podcast.level.base")}</SelectItem>
                  <SelectItem value="medio">{t("tools.podcast.level.medio")}</SelectItem>
                  <SelectItem value="avanzato">{t("tools.podcast.level.avanzato")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("tools.podcast.durationLabel", { min: minutes })}</Label>
              <Slider value={[minutes]} onValueChange={(v) => setMinutes(v[0])} min={3} max={12} step={1} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pod-source">{t("tools.podcast.sourceLabel")}</Label>
            <Textarea
              id="pod-source"
              value={sourceText}
              onChange={(e) => { setSourceText(e.target.value); setAttachedName(null); }}
              rows={3}
              maxLength={20000}
              placeholder={t("tools.podcast.sourcePlaceholder")}
            />
            <MediaAttachButton
              attachedName={attachedName}
              onClear={() => { setAttachedName(null); setSourceText(""); }}
              onText={(text, name) => {
                setSourceText(text);
                setAttachedName(name);
                if (!topic.trim()) setTopic(name.replace(/\.[^.]+$/, "").slice(0, 60));
              }}
            />
          </div>

          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || topic.trim().length < 3}
            className="w-full rounded-xl"
          >
            {mutation.isPending ? (
              <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("tools.podcast.generatingButton")}</>
            ) : (
              <><Sparkles className="mr-1.5 h-4 w-4" /> {t("tools.podcast.generateButton")}</>
            )}
          </Button>
        </div>
      </Card>

      {current && <PodcastPlayer script={current} />}

      {history.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">{t("tools.podcast.historyTitle")}</h3>
          <div className="space-y-2">
            {history.map((h) => (
              <button
                key={h.id}
                onClick={() => setCurrent(h.script as PodcastScript)}
                className="w-full rounded-xl border border-border bg-card p-3 text-left transition hover:bg-muted"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{h.title}</div>
                    <div className="truncate text-xs text-muted-foreground">{h.topic}</div>
                  </div>
                  <Badge variant="secondary" className="shrink-0 text-xs">{t("tools.podcast.historyBadge", { min: h.duration_estimate_min, level: h.level })}</Badge>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const OPENAI_VOICE_IDS = [
  "alloy", "nova", "shimmer", "coral", "sage", "fable", "onyx", "echo", "ash", "ballad", "verse",
] as const;

function PodcastPlayer({ script }: { script: PodcastScript }) {
  const { t } = useTranslation();
  const OPENAI_VOICES = OPENAI_VOICE_IDS.map((id) => ({
    id,
    label: t(`tools.podcast.voices.${id}.label`),
    tone: t(`tools.podcast.voices.${id}.tone`),
  }));
  const [state, setState] = useState<"idle" | "loading" | "playing" | "paused">("idle");
  const [idx, setIdx] = useState(0);
  const [voiceA, setVoiceA] = useState("nova");
  const [voiceB, setVoiceB] = useState("onyx");
  const [speed, setSpeed] = useState(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const cacheRef = useRef<Map<string, string>>(new Map());
  const idxRef = useRef(0);
  const cancelledRef = useRef(false);
  const runSpeak = useServerFn(synthesizeSpeech);

  const lines = useMemo(() => {
    return [
      { speaker: "A" as const, text: script.intro },
      ...script.segments,
      { speaker: "B" as const, text: script.outro },
    ];
  }, [script]);

  // Build chapter list. Use IA-provided chapters if present, else auto-chunk
  // segments into ~4 chapters so the user always has jump points.
  // Line index = 1 + startSegment (intro sits at line 0).
  const chapters = useMemo(() => {
    const segCount = script.segments.length;
    const source =
      script.chapters && script.chapters.length > 0
        ? script.chapters
            .map((c) => ({
              title: c.title,
              startSegment: Math.max(0, Math.min(segCount - 1, c.startSegment)),
            }))
            .sort((a, b) => a.startSegment - b.startSegment)
        : (() => {
            const n = Math.min(4, Math.max(2, Math.ceil(segCount / 4)));
            const step = Math.max(1, Math.floor(segCount / n));
            return Array.from({ length: n }, (_, i) => ({
              title: t("tools.podcast.player.chapterPart", { n: i + 1 }),
              startSegment: i * step,
            }));
          })();
    const items = [
      { title: t("tools.podcast.player.chapterIntro"), lineIdx: 0 },
      ...source.map((c) => ({ title: c.title, lineIdx: 1 + c.startSegment })),
      { title: t("tools.podcast.player.chapterOutro"), lineIdx: 1 + segCount },
    ];
    // Dedupe by lineIdx keeping first occurrence
    const seen = new Set<number>();
    return items.filter((c) => (seen.has(c.lineIdx) ? false : (seen.add(c.lineIdx), true)));
  }, [script]);

  const currentChapter = useMemo(() => {
    let match = 0;
    for (let i = 0; i < chapters.length; i++) {
      if (chapters[i].lineIdx <= idx) match = i;
      else break;
    }
    return match;
  }, [chapters, idx]);

  function jumpToChapter(lineIdx: number) {
    cancelledRef.current = true;
    if (audioRef.current) audioRef.current.pause();
    setTimeout(() => void playFrom(lineIdx), 30);
  }

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      // Revoke cached blob URLs
      cacheRef.current.forEach((url) => URL.revokeObjectURL(url));
      cacheRef.current.clear();
    };
  }, []);

  // Reset cache when voices / speed change so new audio is generated
  useEffect(() => {
    cacheRef.current.forEach((url) => URL.revokeObjectURL(url));
    cacheRef.current.clear();
  }, [voiceA, voiceB, speed]);

  async function getAudioUrl(lineIdx: number): Promise<string> {
    const line = lines[lineIdx];
    const voice = line.speaker === "A" ? voiceA : voiceB;
    const key = `${voice}::${speed}::${lineIdx}`;
    const cached = cacheRef.current.get(key);
    if (cached) return cached;

    const res = await runSpeak({
      data: {
        text: line.text,
        voice,
        speed,
        instructions: t("tools.podcast.player.ttsInstructions"),
      },
    });
    const bin = atob(res.audioBase64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes.buffer as ArrayBuffer], { type: res.mimeType });
    const url = URL.createObjectURL(blob);
    cacheRef.current.set(key, url);
    return url;
  }

  async function playFrom(startIdx: number) {
    cancelledRef.current = false;
    idxRef.current = startIdx;
    setIdx(startIdx);
    setState("loading");

    // Reuse a single Audio element so the OS media session stays attached
    // across lines (required for lock-screen / status-bar controls).
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = "auto";
    }

    while (idxRef.current < lines.length && !cancelledRef.current) {
      const i = idxRef.current;
      setIdx(i);
      let url: string;
      try {
        url = await getAudioUrl(i);
      } catch (e) {
        toast.error((e as Error).message);
        setState("idle");
        return;
      }
      if (cancelledRef.current) return;

      // Prefetch next line in background
      if (i + 1 < lines.length) void getAudioUrl(i + 1).catch(() => {});

      updateMediaSession(i);

      await new Promise<void>((resolve) => {
        const audio = audioRef.current!;
        audio.src = url;
        audio.playbackRate = 1; // speed already applied at synthesis
        audio.onplay = () => {
          setState("playing");
          if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing";
        };
        audio.onpause = () => {
          if ("mediaSession" in navigator && !cancelledRef.current)
            navigator.mediaSession.playbackState = "paused";
        };
        audio.onended = () => resolve();
        audio.onerror = () => resolve();
        audio.play().catch(() => resolve());
      });

      if (cancelledRef.current) return;
      idxRef.current += 1;
    }
    setState("idle");
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "none";
    idxRef.current = 0;
    setIdx(0);
  }

  function updateMediaSession(i: number) {
    if (!("mediaSession" in navigator)) return;
    const line = lines[i];
    // Find the chapter this line belongs to
    let chapIdx = 0;
    for (let k = 0; k < chapters.length; k++) {
      if (chapters[k].lineIdx <= i) chapIdx = k;
      else break;
    }
    const chap = chapters[chapIdx];
    navigator.mediaSession.metadata = new MediaMetadata({
      title: script.title,
      artist: `${chap?.title ?? ""} · ${line.speaker === "A" ? t("tools.podcast.player.hostLabel") : t("tools.podcast.player.expertLabel")}`,
      album: t("tools.podcast.player.albumLabel"),
    });
    navigator.mediaSession.setActionHandler("play", () => {
      audioRef.current?.play().catch(() => {});
    });
    navigator.mediaSession.setActionHandler("pause", () => {
      audioRef.current?.pause();
      setState("paused");
    });
    navigator.mediaSession.setActionHandler("stop", () => stop());
    navigator.mediaSession.setActionHandler("previoustrack", () => {
      // Jump to the previous chapter (or restart current if we're past its start)
      const cur = idxRef.current;
      let target = 0;
      for (let k = chapters.length - 1; k >= 0; k--) {
        if (chapters[k].lineIdx < cur) {
          target = chapters[k].lineIdx;
          break;
        }
      }
      jumpToChapter(target);
    });
    navigator.mediaSession.setActionHandler("nexttrack", () => {
      const cur = idxRef.current;
      const next = chapters.find((c) => c.lineIdx > cur);
      if (next) jumpToChapter(next.lineIdx);
    });
  }

  function togglePause() {
    const a = audioRef.current;
    if (!a) return;
    if (state === "playing") {
      a.pause();
      setState("paused");
      if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused";
    } else if (state === "paused") {
      a.play().catch(() => {});
      setState("playing");
      if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing";
    }
  }

  function stop() {
    cancelledRef.current = true;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    if ("mediaSession" in navigator) {
      navigator.mediaSession.playbackState = "none";
      navigator.mediaSession.metadata = null;
    }
    setState("idle");
    setIdx(0);
  }

  return (
    <Card className="card-soft border-0 p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">{t("tools.tabs.podcast")}</p>
          <h3 className="text-lg font-semibold">{script.title}</h3>
        </div>
        <Badge variant="secondary">{t("tools.podcast.player.tracksBadge", { count: lines.length })}</Badge>
      </div>

      <div className="mb-4 space-y-3 rounded-xl bg-muted/40 p-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">{t("tools.podcast.player.voiceALabel")}</Label>
            <Select value={voiceA} onValueChange={setVoiceA} disabled={state !== "idle"}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OPENAI_VOICES.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    <span className="font-medium">{v.label}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{v.tone}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("tools.podcast.player.voiceBLabel")}</Label>
            <Select value={voiceB} onValueChange={setVoiceB} disabled={state !== "idle"}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OPENAI_VOICES.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    <span className="font-medium">{v.label}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{v.tone}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("tools.podcast.player.speedLabel", { speed: speed.toFixed(2) })}</Label>
          <Slider
            value={[speed]}
            onValueChange={(v) => setSpeed(v[0])}
            min={0.75}
            max={1.5}
            step={0.05}
            disabled={state !== "idle"}
          />
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2">
        {state === "idle" ? (
          <Button onClick={() => playFrom(0)} className="rounded-xl">
            <Play className="mr-1.5 h-4 w-4" /> {t("tools.podcast.player.listen")}
          </Button>
        ) : state === "loading" ? (
          <Button disabled className="rounded-xl">
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("tools.podcast.player.generatingVoice")}
          </Button>
        ) : (
          <>
            <Button onClick={togglePause} variant="secondary" className="rounded-xl">
              {state === "paused" ? <><Play className="mr-1.5 h-4 w-4" /> {t("tools.podcast.player.resume")}</> : <><Pause className="mr-1.5 h-4 w-4" /> {t("tools.podcast.player.pause")}</>}
            </Button>
            <Button onClick={stop} variant="ghost" size="icon" aria-label={t("tools.podcast.player.stopAria")}>
              <Square className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      {/* Capitoli / segnalibri: chip orizzontali per saltare tra sezioni */}
      <div className="mb-3">
        <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
          <Bookmark className="h-3 w-3" /> {t("tools.podcast.player.chaptersLabel")}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {chapters.map((c, i) => {
            const active = i === currentChapter && state !== "idle";
            return (
              <button
                key={c.lineIdx}
                onClick={() => jumpToChapter(c.lineIdx)}
                className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition ${
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background hover:bg-muted"
                }`}
              >
                <span className="mr-1 opacity-70">{i + 1}.</span>
                {c.title}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-h-96 space-y-2 overflow-y-auto rounded-xl bg-muted/50 p-3">
        {lines.map((l, i) => (
          <div key={i}>
            {chapters.some((c) => c.lineIdx === i) && (
              <button
                onClick={() => jumpToChapter(i)}
                className="mb-1.5 mt-2 flex w-full items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-wider text-primary first:mt-0 hover:underline"
              >
                <Bookmark className="h-3 w-3" />
                {chapters.find((c) => c.lineIdx === i)!.title}
              </button>
            )}
            <div
              onClick={() => jumpToChapter(i)}
              className={`cursor-pointer rounded-lg p-2.5 text-sm leading-relaxed transition ${
                i === idx && state !== "idle"
                  ? "bg-primary/10 ring-1 ring-primary/30"
                  : "bg-background hover:bg-background/70"
              }`}
            >
              <span className={`mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white ${l.speaker === "A" ? "bg-sky-500" : "bg-violet-500"}`}>
                {l.speaker}
              </span>
              {l.text}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ============================================================ */
/*  MAPS                                                        */
/* ============================================================ */

function MapsPanel() {
  const { t } = useTranslation();
  return (
    <Tabs defaultValue="ai" className="w-full">
      <TabsList className="flex w-full justify-start gap-1 overflow-x-auto sm:grid sm:grid-cols-5">
        <TabsTrigger value="ai" className="gap-1.5"><Sparkles className="h-3.5 w-3.5" /> {t("tools.map.tabs.ai")}</TabsTrigger>
        <TabsTrigger value="illustrated" className="gap-1.5"><Wand2 className="h-3.5 w-3.5" /> {t("video.styles.illustrated.label")}</TabsTrigger>
        <TabsTrigger value="manual" className="gap-1.5"><Plus className="h-3.5 w-3.5" /> {t("tools.map.tabs.manual")}</TabsTrigger>
        <TabsTrigger value="library" className="gap-1.5"><Library className="h-3.5 w-3.5" /> {t("tools.map.tabs.library")}</TabsTrigger>
      </TabsList>
      <TabsContent value="ai" className="mt-4"><AiMapPanel /></TabsContent>
      <TabsContent value="illustrated" className="mt-4"><IllustratedMapStudio /></TabsContent>
      <TabsContent value="manual" className="mt-4"><ManualMapPanel /></TabsContent>
      <TabsContent value="library" className="mt-4"><MapLibraryPanel /></TabsContent>
    </Tabs>
  );
}

function MapLibraryPanel() {
  const { t } = useTranslation();
  const [viewing, setViewing] = useState<{ title: string; root: MindNode } | null>(null);
  return (
    <div className="space-y-4">
      <FolderedLibrary
        itemsTable="mind_maps"
        foldersTable="mind_map_folders"
        itemSelect="nodes,map_kind,root_topic,source"
        duplicateFields={["nodes", "map_kind", "root_topic", "source"]}
        title={t("tools.map.library.title")}
        itemLabel={t("tools.map.library.itemLabel")}
        onOpen={(row) => {
          const nodes = row.nodes as unknown as MindNode;
          setViewing({ title: row.title, root: nodes });
        }}
      />
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2 pr-6">
              <DialogTitle>{viewing?.title}</DialogTitle>
              {viewing && (
                <MindMapDownloadButton root={viewing.root} title={viewing.title} />
              )}
            </div>
          </DialogHeader>
          {viewing && <MindMapView root={viewing.root} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AiMapPanel() {
  const { t } = useTranslation();
  const [topic, setTopic] = useState("");
  const [kind, setKind] = useState<"mind" | "concept" | "cornell" | "dsa_exam">("mind");
  const [result, setResult] = useState<{ title: string; root: MindNode } | null>(null);
  const [source, setSource] = useState<"topic" | "text" | "audio" | "image">("topic");
  const [sourceText, setSourceText] = useState("");
  const [extracting, setExtracting] = useState<null | "audio" | "image" | "file">(null);
  const [extractedFrom, setExtractedFrom] = useState<string | null>(null);
  const [customRequest, setCustomRequest] = useState("");
  const [refineOpen, setRefineOpen] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState("");
  const run = useServerFn(generateMindMap);
  const runTranscribe = useServerFn(transcribeAudio);
  const runExtract = useServerFn(extractTextFromImage);

  const mutation = useMutation({
    mutationFn: async () =>
      run({
        data: {
          topic,
          kind,
          depth: 3,
          sourceText: source !== "topic" && sourceText.trim() ? sourceText.trim() : undefined,
          customRequest: customRequest.trim() ? customRequest.trim() : undefined,
        },
      }),
    onSuccess: (r) => {
      setResult({ title: r.title, root: r.root as MindNode });
      toast.success(t("tools.map.ai.toasts.mapGenerated"));
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const refine = useMutation({
    mutationFn: async () => {
      if (!result) throw new Error(t("tools.errors.noMapToEdit"));
      return run({
        data: {
          topic,
          kind,
          depth: 3,
          sourceText: source !== "topic" && sourceText.trim() ? sourceText.trim() : undefined,
          previousMap: JSON.stringify({ title: result.title, root: result.root }),
          customRequest: refinePrompt.trim() || t("tools.map.ai.refine.defaultPrompt"),
        },
      });
    },
    onSuccess: (r) => {
      setResult({ title: r.title, root: r.root as MindNode });
      setRefineOpen(false);
      setRefinePrompt("");
      toast.success(t("tools.map.ai.toasts.mapUpdated"));
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  async function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error(t("tools.errors.cannotReadFile")));
      reader.readAsDataURL(file);
    });
  }

  async function handleTextFile(file: File) {
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("tools.errors.textTooLarge"));
      return;
    }
    setExtracting("file");
    try {
      const text = await file.text();
      setSourceText(text.slice(0, 20000));
      setExtractedFrom(file.name);
      if (!topic.trim()) setTopic(file.name.replace(/\.[^.]+$/, "").slice(0, 60));
      toast.success(t("tools.map.ai.toasts.textUploaded"));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setExtracting(null);
    }
  }

  async function handleAudioFile(file: File) {
    if (file.size > 20 * 1024 * 1024) {
      toast.error(t("tools.errors.audioTooLarge"));
      return;
    }
    setExtracting("audio");
    try {
      const b64 = await fileToBase64(file);
      const { text } = await runTranscribe({
        data: { base64: b64, mimeType: file.type || "audio/webm", filename: file.name },
      });
      setSourceText(text.slice(0, 20000));
      setExtractedFrom(file.name);
      if (!topic.trim()) setTopic(file.name.replace(/\.[^.]+$/, "").slice(0, 60));
      toast.success(t("tools.map.ai.toasts.audioTranscribed"));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setExtracting(null);
    }
  }

  async function handleImageFile(file: File) {
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("tools.errors.imageTooLarge"));
      return;
    }
    setExtracting("image");
    try {
      const b64 = await fileToBase64(file);
      const { text } = await runExtract({
        data: { base64: b64, mimeType: file.type || "image/jpeg" },
      });
      setSourceText(text.slice(0, 20000));
      setExtractedFrom(file.name);
      if (!topic.trim()) setTopic(file.name.replace(/\.[^.]+$/, "").slice(0, 60));
      toast.success(t("tools.map.ai.toasts.imageExtracted"));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setExtracting(null);
    }
  }

  return (
    <div className="space-y-4">
      <Card className="card-soft border-0 p-5">
        <div className="mb-4 flex items-start gap-3">
          <div className="rounded-2xl bg-primary/10 p-2 text-primary"><Sparkles className="h-5 w-5" /></div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold">{t("tools.map.ai.title")}</p>
            </div>
            <p className="text-sm text-muted-foreground">{t("tools.map.ai.desc")}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("tools.map.ai.sourceLabel")}</Label>
            <Tabs value={source} onValueChange={(v) => setSource(v as typeof source)}>
              <TabsList className="flex w-full justify-start gap-1 overflow-x-auto sm:grid sm:grid-cols-5">
                <TabsTrigger value="topic" className="gap-1 text-xs"><Type className="h-3.5 w-3.5" /> {t("tools.map.ai.source.topic")}</TabsTrigger>
                <TabsTrigger value="text" className="gap-1 text-xs"><FileText className="h-3.5 w-3.5" /> {t("tools.map.ai.source.text")}</TabsTrigger>
                <TabsTrigger value="audio" className="gap-1 text-xs"><Mic className="h-3.5 w-3.5" /> {t("tools.map.ai.source.audio")}</TabsTrigger>
                <TabsTrigger value="image" className="gap-1 text-xs"><ImageIcon className="h-3.5 w-3.5" /> {t("tools.map.ai.source.image")}</TabsTrigger>
              </TabsList>

              <TabsContent value="text" className="mt-3 space-y-2">
                <Textarea
                  value={sourceText}
                  onChange={(e) => { setSourceText(e.target.value); setExtractedFrom(null); }}
                  placeholder={t("tools.map.ai.text.placeholder")}
                  rows={6}
                  maxLength={20000}
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <label className="inline-flex cursor-pointer items-center gap-1 text-primary hover:underline">
                    <Upload className="h-3.5 w-3.5" /> {t("tools.map.ai.text.uploadFile")}
                    <input
                      type="file"
                      accept=".txt,.md,text/plain,text/markdown"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleTextFile(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  <span>{t("tools.map.ai.text.charsCount", { count: sourceText.length })}</span>
                </div>
              </TabsContent>

              <TabsContent value="audio" className="mt-3 space-y-2">
                <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/30 p-6 text-center transition hover:bg-muted/50">
                  {extracting === "audio" ? (
                    <><Loader2 className="h-6 w-6 animate-spin text-primary" /><span className="text-sm text-muted-foreground">{t("tools.map.ai.audio.transcribing")}</span></>
                  ) : (
                    <>
                      <Mic className="h-6 w-6 text-primary" />
                      <span className="text-sm font-medium">{t("tools.map.ai.audio.uploadLabel")}</span>
                      <span className="text-xs text-muted-foreground">{t("tools.map.ai.audio.formats")}</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="audio/*"
                    className="hidden"
                    disabled={extracting !== null}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handleAudioFile(f);
                      e.target.value = "";
                    }}
                  />
                </label>
                {sourceText && (
                  <div className="rounded-xl bg-muted/50 p-3 text-xs">
                    {extractedFrom && <p className="mb-1 font-medium">{t("tools.map.ai.fromLabel", { name: extractedFrom })}</p>}
                    <p className="line-clamp-4 text-muted-foreground">{sourceText}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="image" className="mt-3 space-y-2">
                <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/30 p-6 text-center transition hover:bg-muted/50">
                  {extracting === "image" ? (
                    <><Loader2 className="h-6 w-6 animate-spin text-primary" /><span className="text-sm text-muted-foreground">{t("tools.map.ai.image.reading")}</span></>
                  ) : (
                    <>
                      <ImageIcon className="h-6 w-6 text-primary" />
                      <span className="text-sm font-medium">{t("tools.map.ai.image.uploadLabel")}</span>
                      <span className="text-xs text-muted-foreground">{t("tools.map.ai.image.formats")}</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={extracting !== null}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handleImageFile(f);
                      e.target.value = "";
                    }}
                  />
                </label>
                {sourceText && (
                  <div className="rounded-xl bg-muted/50 p-3 text-xs">
                    {extractedFrom && <p className="mb-1 font-medium">{t("tools.map.ai.fromLabel", { name: extractedFrom })}</p>}
                    <p className="line-clamp-4 text-muted-foreground">{sourceText}</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          <div className="space-y-1.5">
            <Label>{source === "topic" ? t("tools.map.ai.topicLabel") : t("tools.map.ai.mapTitleLabel")}</Label>
            <Input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={source === "topic" ? t("tools.map.ai.topicPlaceholder") : t("tools.map.ai.mapTitlePlaceholder")}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("tools.map.ai.typeLabel")}</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mind">{t("tools.map.ai.kind.mind")}</SelectItem>
                <SelectItem value="concept">{t("tools.map.ai.kind.concept")}</SelectItem>
                <SelectItem value="cornell">{t("tools.map.ai.kind.cornell")}</SelectItem>
                <SelectItem value="dsa_exam">
                  <span className="inline-flex items-center gap-1.5">
                    <Accessibility className="h-3.5 w-3.5" /> {t("tools.map.ai.kind.dsa")}
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            {kind === "dsa_exam" && (
              <p className="rounded-lg bg-primary/5 p-2 text-xs leading-relaxed text-foreground/80">
                {t("tools.map.ai.dsaNote")}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <Wand2 className="h-3.5 w-3.5" /> {t("tools.map.ai.customRequestLabel")} <span className="text-xs font-normal text-muted-foreground">{t("tools.map.ai.optional")}</span>
            </Label>
            <Textarea
              value={customRequest}
              onChange={(e) => setCustomRequest(e.target.value)}
              placeholder={
                kind === "dsa_exam"
                  ? t("tools.map.ai.customRequestPlaceholderDsa")
                  : t("tools.map.ai.customRequestPlaceholderDefault")
              }
              rows={2}
              maxLength={600}
            />
          </div>
          <Button
            onClick={() => mutation.mutate()}
            disabled={
              mutation.isPending ||
              extracting !== null ||
              topic.trim().length < 3 ||
              (source !== "topic" && sourceText.trim().length < 20)
            }
            className="w-full rounded-xl"
          >
            {mutation.isPending ? (
              <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("tools.map.ai.generatingButton")}</>
            ) : (
              <><Sparkles className="mr-1.5 h-4 w-4" /> {t("tools.map.ai.generateButton")}</>
            )}
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="card-soft border-0 p-3">
          <div className="mb-2 flex items-center justify-between gap-2 px-3 pt-2">
            <h3 className="text-sm font-semibold">{result.title}</h3>
            {kind === "dsa_exam" && (
              <Badge variant="secondary" className="gap-1 text-xs">
                <Accessibility className="h-3 w-3" /> {t("tools.map.ai.dsaBadge")}
              </Badge>
            )}
          </div>
          <MindMapView root={result.root} />
          <div className="mt-3 flex flex-wrap gap-2 px-3 pb-2">
            <Button
              size="sm"
              variant="secondary"
              className="rounded-xl"
              onClick={() => setRefineOpen(true)}
            >
              <Wand2 className="mr-1.5 h-3.5 w-3.5" /> {t("tools.map.ai.editRequestButton")}
            </Button>
            <MindMapDownloadButton root={result.root} title={result.title} />
          </div>
        </Card>
      )}

      <Dialog open={refineOpen} onOpenChange={setRefineOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("tools.map.ai.refine.title")}</DialogTitle>
            <DialogDescription>
              {t("tools.map.ai.refine.desc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={refinePrompt}
              onChange={(e) => setRefinePrompt(e.target.value)}
              placeholder={t("tools.map.ai.refine.placeholder")}
              rows={4}
              maxLength={600}
            />
            <div className="flex flex-wrap gap-1.5">
              {[
                t("tools.map.ai.refine.suggestions.simplify"),
                t("tools.map.ai.refine.suggestions.addDates"),
                t("tools.map.ai.refine.suggestions.shorterLabels"),
                t("tools.map.ai.refine.suggestions.moreVisualSymbols"),
                t("tools.map.ai.refine.suggestions.essentialOnly"),
              ].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() =>
                    setRefinePrompt((p) => (p ? `${p}\n${s}` : s))
                  }
                  className="rounded-full border border-border bg-background px-2.5 py-1 text-xs text-foreground/70 hover:bg-muted"
                >
                  + {s}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              className="w-full rounded-xl"
              onClick={() => refine.mutate()}
              disabled={refine.isPending}
            >
              {refine.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("tools.map.ai.refine.updatingButton")}</>
              ) : (
                <><Sparkles className="mr-2 h-4 w-4" /> {t("tools.map.ai.refine.updateButton")}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

type Branch = { id: string; label: string; sub: string[] };

function ManualMapPanel() {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [center, setCenter] = useState("");
  const [branches, setBranches] = useState<Branch[]>([
    { id: "b1", label: "", sub: [""] },
    { id: "b2", label: "", sub: [""] },
    { id: "b3", label: "", sub: [""] },
  ]);
  const [saving, setSaving] = useState(false);

  const root: MindNode = useMemo(
    () => ({
      id: "root",
      label: center || t("tools.map.manual.defaultCenter"),
      children: branches
        .filter((b) => b.label.trim())
        .map((b) => ({
          id: b.id,
          label: b.label,
          children: b.sub
            .filter((s) => s.trim())
            .map((s, i) => ({ id: `${b.id}-${i}`, label: s })),
        })),
    }),
    [center, branches],
  );

  function addBranch() {
    setBranches((b) => [...b, { id: `b${b.length + 1}`, label: "", sub: [""] }]);
  }
  function removeBranch(id: string) {
    setBranches((b) => b.filter((x) => x.id !== id));
  }
  function updateBranch(id: string, label: string) {
    setBranches((b) => b.map((x) => (x.id === id ? { ...x, label } : x)));
  }
  function addSub(id: string) {
    setBranches((b) => b.map((x) => (x.id === id ? { ...x, sub: [...x.sub, ""] } : x)));
  }
  function updateSub(id: string, i: number, v: string) {
    setBranches((b) =>
      b.map((x) => (x.id === id ? { ...x, sub: x.sub.map((s, k) => (k === i ? v : s)) } : x)),
    );
  }

  async function save() {
    if (!center.trim() || !title.trim()) {
      toast.error(t("tools.errors.missingTitleCenter"));
      return;
    }
    setSaving(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) { setSaving(false); return; }
    const { error } = await supabase.from("mind_maps").insert({
      user_id: u.user.id,
      title,
      map_kind: "mind",
      source: "manual",
      root_topic: center,
      nodes: JSON.parse(JSON.stringify(root)),
    });
    setSaving(false);
    if (error) toast.error(error.message);
    else toast.success(t("tools.map.manual.toastSaved"));
  }

  return (
    <div className="space-y-4">
      <Card className="card-soft border-0 p-5">
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("tools.map.manual.titleLabel")}</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("tools.map.manual.titlePlaceholder")} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("tools.map.manual.centerLabel")}</Label>
              <Input value={center} onChange={(e) => setCenter(e.target.value)} placeholder={t("tools.map.manual.centerPlaceholder")} />
            </div>
          </div>

          <div className="space-y-3 pt-1">
            {branches.map((b) => (
              <div key={b.id} className="rounded-xl border border-border p-3">
                <div className="flex items-center gap-2">
                  <Input
                    value={b.label}
                    onChange={(e) => updateBranch(b.id, e.target.value)}
                    placeholder={t("tools.map.manual.branchPlaceholder")}
                    className="font-medium"
                  />
                  <Button size="icon" variant="ghost" onClick={() => removeBranch(b.id)} aria-label={t("tools.map.manual.removeBranchAria")}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="mt-2 space-y-1.5 pl-4">
                  {b.sub.map((s, i) => (
                    <Input
                      key={i}
                      value={s}
                      onChange={(e) => updateSub(b.id, i, e.target.value)}
                      placeholder={t("tools.map.manual.subPlaceholder", { n: i + 1 })}
                      className="h-8 text-sm"
                    />
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => addSub(b.id)} className="h-7 px-2 text-xs">
                    <Plus className="mr-1 h-3 w-3" /> {t("tools.map.manual.addSubButton")}
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addBranch} className="w-full rounded-xl">
              <Plus className="mr-1.5 h-4 w-4" /> {t("tools.map.manual.addBranchButton")}
            </Button>
          </div>

          <Button onClick={save} disabled={saving} className="w-full rounded-xl">
            {saving ? t("tools.map.manual.savingButton") : t("tools.map.manual.saveButton")}
          </Button>
        </div>
      </Card>

      <Card className="card-soft border-0 p-3">
        <div className="mb-2 flex items-center justify-between gap-2 px-3 pt-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("tools.map.manual.previewLabel")}</p>
          <MindMapDownloadButton root={root} title={title || "mappa"} />
        </div>
        <MindMapView root={root} />
      </Card>
    </div>
  );
}

/* ============================================================ */
/*  GUIDE — HOW TO MAKE A MAP                                   */
/* ============================================================ */

function MapsGuide() {
  const { t } = useTranslation();
  const mindMapPillars = t("tools.guide.mindMap.pillars", { returnObjects: true }) as string[];
  const conceptMapPillars = t("tools.guide.conceptMap.pillars", { returnObjects: true }) as string[];
  const cornellPillars = t("tools.guide.cornell.pillars", { returnObjects: true }) as string[];
  const methodSteps = t("tools.guide.method.steps", { returnObjects: true }) as { title: string; desc: string }[];
  return (
    <div className="space-y-4">
      <Card className="card-soft border-0 p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-primary/10 p-2 text-primary"><Lightbulb className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold">{t("tools.guide.whyTitle")}</h3>
            <p className="mt-1 text-sm text-foreground/80">
              {t("tools.guide.whyDesc")}
            </p>
          </div>
        </div>
      </Card>

      <GuideBlock
        icon={Network}
        kind={t("tools.guide.mindMap.kind")}
        color="sky"
        pillars={mindMapPillars}
        when={t("tools.guide.mindMap.when")}
        example={t("tools.guide.mindMap.example")}
      />

      <GuideBlock
        icon={Layers}
        kind={t("tools.guide.conceptMap.kind")}
        color="sage"
        pillars={conceptMapPillars}
        when={t("tools.guide.conceptMap.when")}
        example={t("tools.guide.conceptMap.example")}
      />

      <GuideBlock
        icon={BookOpen}
        kind={t("tools.guide.cornell.kind")}
        color="lilac"
        pillars={cornellPillars}
        when={t("tools.guide.cornell.when")}
        example={t("tools.guide.cornell.example")}
      />

      <Card className="card-soft border-0 p-5">
        <h3 className="font-semibold">{t("tools.guide.method.title")}</h3>
        <ol className="mt-3 space-y-3 text-sm">
          {methodSteps.map((step, i) => (
            <Step key={i} n={i + 1} title={step.title}>{step.desc}</Step>
          ))}
        </ol>
      </Card>

    </div>
  );
}

function GuideBlock({
  icon: Icon,
  kind,
  color,
  pillars,
  when,
  example,
}: {
  icon: typeof Network;
  kind: string;
  color: string;
  pillars: string[];
  when: string;
  example: string;
}) {
  const { t } = useTranslation();
  return (
    <Card className="card-soft border-0 p-5">
      <div className="mb-3 flex items-center gap-3">
        <div className={`pastel-${color} rounded-2xl p-2`}>
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="font-semibold">{kind}</h3>
      </div>
      <ul className="space-y-1.5 text-sm text-foreground/80">
        {pillars.map((p, i) => (
          <li key={i} className="flex gap-2"><span className="text-primary">•</span> {p}</li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-muted-foreground"><b>{t("tools.guide.whenLabel")}</b> {when}</p>
      <pre className="mt-3 overflow-x-auto rounded-xl bg-muted p-3 text-xs leading-snug text-foreground/70">{example}</pre>
    </Card>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{n}</span>
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-foreground/70">{children}</p>
      </div>
    </li>
  );
}